import React, { useEffect, useState, useCallback } from "react";
import Breadcrumbs from "../../../components/Breadcrumbs";
import { MediaProvider, useMedia } from "../../../contexts/MediaContext";
import MediaSelector from "../../../components/website/MediaSelector";
import MediaGallery from "../../../components/website/MediaGallery";
import ResizableEditArea from "../../../components/common/ResizableEditArea";
import StatusMessage from "../../../components/common/StatusMessage";
import {
  MESSAGE_TYPES,
  handleSimpleInputChange,
} from "../../../utils/editIframe";
import { useDragAndDrop } from "../../../hooks/useDragAndDrop";
import Iframe from "../../../components/Iframe";
import {
  clearValidationErrors,
  highlightErrorFields,
  validateFields,
  clearFieldError,
} from "../../../utils/formValidation";

const breadcrumbs = [
  { title: "Home", path: `/` },
  { title: "Instructor", path: `/instructor` },
];

function InstructorEditPageContent() {
  const { isMediaOpen, fetchMediaFiles, selectMedia } = useMedia();
  const contentKey = "instructorContent";
  const [message, setMessage] = useState("");
  const [loadingText, setLoadingText] = useState("");
  const [pageContent, setPageContent] = useState({
    page_image: {},
    page_content: "",
    instructors: [],
  });

  // ドラッグ&ドロップ並べ替え処理
  const handleInstructorReorder = useCallback(
    (reorderedItems) => {
      setPageContent((prev) => {
        const newData = {
          ...prev,
          instructors: reorderedItems.map((instructor, index) => ({
            ...instructor,
            display_order: index,
          })),
        };

        clearValidationErrors();

        // iframeに全データを送信
        const iframe = document.querySelector("iframe");

        if (iframe?.contentWindow) {
          const message = {
            type: MESSAGE_TYPES.TEXT_UPDATE,
            contentKey,
            allData: newData,
            isFullUpdate: true,
          };

          iframe.contentWindow.postMessage(
            message,
            process.env.REACT_APP_ORIGIN
          );

          // 確認のため、少し遅延してもう一度送信
          setTimeout(() => {
            console.log("Sending delayed message to iframe:", message);
            iframe.contentWindow.postMessage(
              message,
              process.env.REACT_APP_ORIGIN
            );
          }, 100);
        } else {
          console.warn("iframe not found or contentWindow not available:", {
            iframe: !!iframe,
            contentWindow: !!iframe?.contentWindow,
          });
        }

        return newData;
      });
    },
    [contentKey]
  );

  // ドラッグ&ドロップフック
  const { getDraggableProps } = useDragAndDrop(
    pageContent.instructors || [],
    handleInstructorReorder
  );

  // データ取得処理
  const fetchPageContentData = useCallback(async () => {
    setLoadingText("読み込み中");
    try {
      const apiUrl = process.env.REACT_APP_API_URL;
      const response = await fetch(
        `${apiUrl}/api/get_page_instructor_content/`
      );
      if (!response.ok) throw new Error("ネットワークレスポンスが異常です");
      const data = await response.json();
      console.log("instructor data", data);
      setPageContent(data);
    } catch (error) {
      console.error("データ取得エラー:", error);
    }
    setLoadingText("");
  }, []);

  useEffect(() => {
    fetchPageContentData();
    fetchMediaFiles();
  }, [fetchPageContentData, fetchMediaFiles]);

  // バリデーションルールを定義
  const validationRules = {
    page_content: { required: true },
  };

  // 更新処理
  const handlePageDataSubmit = async () => {
    clearValidationErrors();

    // 基本フィールドのバリデーション
    const errorFields = validateFields(pageContent, validationRules);

    // インストラクターの検証
    if (!pageContent.instructors || pageContent.instructors.length === 0) {
      alert("インストラクターは少なくとも1人必要です");
      return;
    }

    // インストラクターの詳細バリデーション
    pageContent.instructors.forEach((instructor, index) => {
      if (!instructor.name || instructor.name.trim() === "") {
        errorFields.push(`instructor${index}_name`);
      }
    });

    if (errorFields.length > 0) {
      const fieldLabels = {
        page_content: "インストラクターページ本文",
      };

      // インストラクターのラベルを動的に追加
      pageContent.instructors.forEach((_, index) => {
        fieldLabels[`instructor${index}_name`] = `インストラクター${
          index + 1
        }の名前`;
      });

      highlightErrorFields(errorFields, { fieldLabels });
      setMessage(
        "入力に不備があります。赤くハイライトされた項目を確認してください。"
      );
      setTimeout(() => setMessage(""), 5000);
      return;
    }

    const formData = { page_content: pageContent };

    setLoadingText("保存中");
    try {
      const apiUrl = process.env.REACT_APP_API_URL;
      const response = await fetch(
        `${apiUrl}/api/update_page_instructor_content/`,
        {
          method: "POST",
          credentials: "include",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(formData),
        }
      );

      const data = await response.json();
      if (!response.ok) throw new Error(data.message || "更新に失敗しました");

      setMessage(data.message || "正常に更新されました");
      await fetchPageContentData();

      setTimeout(() => setMessage(""), 3000);
    } catch (error) {
      console.error("更新エラー:", error);
      alert(`更新に失敗しました: ${error.message}`);
    }
    setLoadingText("");
  };

  // リセット処理
  const handleReset = () => {
    clearValidationErrors();
    fetchPageContentData();
  };

  // 入力変更処理（単一フィールド用）
  const handleInputChange = (e) => {
    // エラークラスをクリア
    clearFieldError(e.target);

    handleSimpleInputChange(e, {
      contentKey,
      setData: setPageContent,
    });
  };

  // インストラクター入力変更処理
  const handleInstructorInputChange = (e, index, fieldName) => {
    const { value } = e.target;
    const eventType = e.type;

    // エラークラスをクリア
    clearFieldError(e.target);

    setPageContent((prev) => {
      const newData = {
        ...prev,
        instructors: prev.instructors.map((instructor, i) =>
          i === index ? { ...instructor, [fieldName]: value } : instructor
        ),
      };

      // iframeにメッセージ送信
      const iframe = document.querySelector("iframe");
      if (iframe?.contentWindow) {
        const targetClass = `.edit_instructor_${index}_${fieldName}`;
        const messageType =
          eventType === "focus"
            ? MESSAGE_TYPES.SCROLL_TO
            : MESSAGE_TYPES.TEXT_UPDATE;

        // instagram、x、profileの場合はインストラクターを先に開く
        if (
          eventType === "focus" &&
          ["instagram", "x", "profile"].includes(fieldName)
        ) {
          const selectMessage = {
            type: MESSAGE_TYPES.SELECT_INSTRUCTOR,
            contentKey,
            index,
            instructor: newData.instructors[index],
            tabValue: index.toString(), // indexをtabValueとして送信
          };
          console.log("Sending SELECT_INSTRUCTOR message:", selectMessage);
          iframe.contentWindow.postMessage(
            selectMessage,
            process.env.REACT_APP_ORIGIN
          );

          // インストラクターが開かれてからスクロール処理を実行
          setTimeout(() => {
            iframe.contentWindow.postMessage(
              {
                type: messageType,
                target: targetClass,
                contentKey,
                fieldName,
                index,
                value,
                allData: newData,
              },
              process.env.REACT_APP_ORIGIN
            );
          }, 500); // 500ms待ってからスクロール
        } else {
          // 通常の処理（SELECT_INSTRUCTORが不要な場合）
          iframe.contentWindow.postMessage(
            {
              type: messageType,
              target: targetClass,
              contentKey,
              fieldName,
              index,
              value,
              allData: newData,
            },
            process.env.REACT_APP_ORIGIN
          );
        }
      }

      return newData;
    });
  };

  // インストラクター画像変更処理
  const handleInstructorImageChange = (index, fieldName, media) => {
    setPageContent((prev) => {
      const newData = {
        ...prev,
        instructors: prev.instructors.map((instructor, i) =>
          i === index ? { ...instructor, [fieldName]: media } : instructor
        ),
      };

      // iframeにメッセージ送信
      const iframe = document.querySelector("iframe");
      if (iframe?.contentWindow) {
        const targetClass = `.edit_instructor_${index}_${fieldName}`;

        iframe.contentWindow.postMessage(
          {
            type: MESSAGE_TYPES.IMAGE_UPDATE,
            target: targetClass,
            contentKey,
            fieldName,
            index,
            value: media,
            allData: newData,
          },
          process.env.REACT_APP_ORIGIN
        );
      }

      return newData;
    });
  };

  // ジャンル変更処理（スラッシュ区切り文字列）
  const handleGenreChange = (instructorIndex, value, eventType = "change") => {
    setPageContent((prev) => {
      const newData = {
        ...prev,
        instructors: prev.instructors.map((instructor, i) => {
          if (i === instructorIndex) {
            return { ...instructor, genres: value };
          }
          return instructor;
        }),
      };

      // iframeにメッセージ送信
      const iframe = document.querySelector("iframe");
      if (iframe?.contentWindow) {
        const targetClass = `.edit_instructor_${instructorIndex}_genres`;
        const messageType =
          eventType === "focus"
            ? MESSAGE_TYPES.SCROLL_TO
            : MESSAGE_TYPES.TEXT_UPDATE;

        iframe.contentWindow.postMessage(
          {
            type: messageType,
            target: targetClass,
            contentKey,
            fieldName: "genres",
            index: instructorIndex,
            value: value,
            allData: newData,
          },
          process.env.REACT_APP_ORIGIN
        );
      }

      return newData;
    });
  };

  // インストラクター追加
  const addInstructor = (insertAfterIndex = null) => {
    setPageContent((prev) => {
      const newItem = {
        name: "",
        profile: "",
        image: {},
        instagram: "",
        x: "",
        genres: "",
        display_order: 0,
      };

      let newInstructors;
      if (insertAfterIndex !== null) {
        // 指定位置に挿入
        newInstructors = [
          ...prev.instructors.slice(0, insertAfterIndex + 1),
          newItem,
          ...prev.instructors.slice(insertAfterIndex + 1),
        ];
      } else {
        // 末尾に追加
        newInstructors = [...prev.instructors, newItem];
      }

      // display_orderをインデックスに基づいて更新
      newInstructors = newInstructors.map((instructor, index) => ({
        ...instructor,
        display_order: index,
      }));

      const newData = {
        ...prev,
        instructors: newInstructors,
      };

      // iframeに全データを送信
      const iframe = document.querySelector("iframe");
      if (iframe?.contentWindow) {
        iframe.contentWindow.postMessage(
          {
            type: MESSAGE_TYPES.TEXT_UPDATE,
            contentKey,
            allData: newData,
          },
          process.env.REACT_APP_ORIGIN
        );
      }

      return newData;
    });
  };

  // インストラクター削除
  const removeInstructor = (index) => {
    if (pageContent.instructors.length <= 1) {
      alert("インストラクターは最低1人必要です");
      return;
    }

    if (!window.confirm("このインストラクターを削除してもよろしいですか？")) {
      return;
    }

    setPageContent((prev) => {
      const newData = {
        ...prev,
        instructors: prev.instructors.filter((_, i) => i !== index),
      };

      // iframeに全データを送信
      const iframe = document.querySelector("iframe");
      if (iframe?.contentWindow) {
        iframe.contentWindow.postMessage(
          {
            type: MESSAGE_TYPES.TEXT_UPDATE,
            contentKey,
            allData: newData,
          },
          process.env.REACT_APP_ORIGIN
        );
      }

      return newData;
    });
  };

  // メディア選択処理（ページ画像用）
  const handleMediaSelect = (fieldName, media) => {
    setPageContent((prev) => {
      const newData = {
        ...prev,
        [fieldName]: media,
      };

      // iframeにメッセージ送信
      const iframe = document.querySelector("iframe");
      if (iframe?.contentWindow) {
        const targetClass = `.edit_${fieldName}`;

        iframe.contentWindow.postMessage(
          {
            type: MESSAGE_TYPES.IMAGE_UPDATE,
            target: targetClass,
            contentKey,
            fieldName,
            value: media,
            allData: newData,
          },
          process.env.REACT_APP_ORIGIN
        );
      }

      return newData;
    });
  };

  // ファイルドロップ処理
  const handleDrop = useCallback(
    async (e) => {
      e.preventDefault();
      setLoadingText("アップロード中");
      // ドラッグ&ドロップまたはファイル選択の両方に対応
      const files = e.dataTransfer?.files || e.target?.files;
      const file = files?.[0];

      if (!file) {
        setLoadingText("");
        return;
      }

      try {
        const formData = new FormData();
        formData.append("images", file);
        const apiUrl = process.env.REACT_APP_API_URL;
        const response = await fetch(`${apiUrl}/api/image_upload/`, {
          method: "POST",
          credentials: "include",
          body: formData,
        });

        const data = await response.json();
        if (!response.ok) throw new Error(data.message);

        await fetchMediaFiles();
        if (data.images?.[0]) {
          selectMedia(data.images[0]);
        }
      } catch (error) {
        console.error("アップロードエラー:", error);
        alert("アップロードに失敗しました");
      }
      setLoadingText("");
    },
    [fetchMediaFiles, selectMedia]
  );

  // スタイル要素を作成
  useEffect(() => {
    const styleElement = document.createElement("style");
    styleElement.textContent = `
        .loop {
          grid-template-columns: auto !important;
        }
      `;

    // head に追加
    document.head.appendChild(styleElement);

    // クリーンアップ関数
    return () => {
      document.head.removeChild(styleElement);
    };
  }, []);

  return (
    <>
      <ResizableEditArea>
        {({ onResizeStart }) => (
          <>
            <Breadcrumbs
              breadcrumbs={breadcrumbs}
              onResizeStart={onResizeStart}
            />
            <StatusMessage message={message} type="success" />

            <div className="form">
              {/* ページ上部 */}
              <section>
                <div className="area3">
                  <div className="item">
                    <div className="forms">
                      <textarea
                        className="input2"
                        name="page_content"
                        value={pageContent.page_content}
                        onChange={handleInputChange}
                        onInput={handleInputChange}
                        onFocus={handleInputChange}
                        placeholder="ページ本文"
                        data-label="ページ本文"
                        data-required="true"
                      />
                      <div className="input3 img-form">
                        <MediaSelector
                          label="ページ画像"
                          aspectRatio="9:16"
                          type="image"
                          fieldName="page_image"
                          selectedMedia={pageContent.page_image}
                          onSelect={(media) =>
                            handleMediaSelect("page_image", media)
                          }
                        />
                      </div>
                    </div>
                  </div>
                </div>
              </section>

              {/* インストラクター一覧 */}
              <section>
                <div className="area2">
                  {pageContent.instructors?.map((instructor, index) => (
                    <div
                      className="item"
                      key={index}
                      {...getDraggableProps(index)}
                    >
                      <div className="ctrl">
                        <p>{index + 1}</p>
                        <div className="icon-wrap">
                          <i
                            className="icon delete"
                            onClick={() => removeInstructor(index)}
                          ></i>
                          <i
                            className="icon add"
                            onClick={() => addInstructor(index)}
                          ></i>
                        </div>
                      </div>
                      <div className="forms loop">
                        <div className="area3">
                          <div className="item">
                            <div className="forms">
                              <input
                                className="namae"
                                type="text"
                                name={`instructor${index}_name`}
                                value={instructor.name}
                                onChange={(e) =>
                                  handleInstructorInputChange(e, index, "name")
                                }
                                onFocus={(e) =>
                                  handleInstructorInputChange(e, index, "name")
                                }
                                placeholder="名前"
                              />
                              <input
                                className="genre"
                                type="text"
                                name={`instructor${index}_genres`}
                                value={instructor.genres || ""}
                                onChange={(e) =>
                                  handleGenreChange(
                                    index,
                                    e.target.value,
                                    "change"
                                  )
                                }
                                onFocus={(e) =>
                                  handleGenreChange(
                                    index,
                                    e.target.value,
                                    "focus"
                                  )
                                }
                                placeholder="HIP HOP / LOCK"
                              />
                              <textarea
                                className="input2"
                                name={`instructor${index}_profile`}
                                value={instructor.profile}
                                onChange={(e) =>
                                  handleInstructorInputChange(
                                    e,
                                    index,
                                    "profile"
                                  )
                                }
                                onFocus={(e) =>
                                  handleInstructorInputChange(
                                    e,
                                    index,
                                    "profile"
                                  )
                                }
                                placeholder="プロフィール"
                                rows={5}
                              />
                              <div className="input3 img-form">
                                <MediaSelector
                                  label="画像"
                                  aspectRatio="1:1"
                                  type="image"
                                  fieldName={`instructor${index}_image`}
                                  selectedMedia={instructor.image}
                                  onSelect={(media) =>
                                    handleInstructorImageChange(
                                      index,
                                      "image",
                                      media
                                    )
                                  }
                                />
                              </div>
                            </div>
                          </div>
                        </div>

                        <input
                          type="text"
                          name={`instructor${index}_instagram`}
                          value={instructor.instagram || ""}
                          onChange={(e) =>
                            handleInstructorInputChange(e, index, "instagram")
                          }
                          onFocus={(e) =>
                            handleInstructorInputChange(e, index, "instagram")
                          }
                          placeholder="Instagram URL"
                        />

                        <input
                          type="text"
                          name={`instructor${index}_x`}
                          value={instructor.x || ""}
                          onChange={(e) =>
                            handleInstructorInputChange(e, index, "x")
                          }
                          onFocus={(e) =>
                            handleInstructorInputChange(e, index, "x")
                          }
                          placeholder="X URL"
                        />
                      </div>
                    </div>
                  ))}
                </div>
              </section>
            </div>

            <div className="submit-area">
              <button type="button" onClick={handleReset}>
                Reset
              </button>
              <button type="button" onClick={handlePageDataSubmit}>
                Submit
              </button>
            </div>

            {isMediaOpen && <MediaGallery onUpload={handleDrop} />}
          </>
        )}
      </ResizableEditArea>

      {loadingText && (
        <div className="overlay">
          <div className="loading">
            <div className="circle"></div>
            <p>{loadingText}...</p>
          </div>
        </div>
      )}

      <Iframe path="/instructor" data={pageContent} contentKey={contentKey} />
    </>
  );
}

export default function InstructorEditPage() {
  return (
    <MediaProvider>
      <InstructorEditPageContent />
    </MediaProvider>
  );
}
